/* eslint-disable @angular-eslint/directive-class-suffix */
import {ChangeDetectorRef, Directive, OnChanges, OnInit} from '@angular/core';
import {FragmentService, LockService, Logger, UserService} from '@nh/angular-tses';
import {DepartureInformationFragment} from '../../../../types/departure-information-fragment';
import {BaseFormComponent} from '../base-form.component';

export interface Option<T> {
  value: T;
  displayName: string;
}

export interface OptionsProvider<T> {
  getOptions(): Promise<Option<T>[]>;
}

export interface OptionProvider<T> extends OptionsProvider<T> {
  getOption(value: string): Promise<Option<T>>;
}

export function instanceOfOptionProvider<T>(optionsProvider: OptionsProvider<T>): optionsProvider is OptionProvider<T> {
  return 'getOption' in optionsProvider;
}

/**
 * Base class for select forms for the departure information page.
 *
 * @tparam {T}   Typed param for the value of the options
 */
@Directive()
export abstract class DepartureSelectComponent<T> extends BaseFormComponent implements OnInit, OnChanges {
  public options: Option<T>[];

  constructor(
    protected _fragmentService: FragmentService,
    protected _lockService: LockService,
    protected _userService: UserService,
    protected _cdr: ChangeDetectorRef,
    protected _optionsProvider: OptionsProvider<T>
  ) {
    super(_fragmentService, _lockService, _userService, _cdr);
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.updateOptions();
  }

  /**
   * @Override
   * This allows us to update the options on fragment changes.
   */
  protected _patchValue(fragment: DepartureInformationFragment, emitEvent: boolean = false): void {
    super._patchValue(fragment, emitEvent);
    this.updateOptions();
  }

  /**
   * @override
   */
  protected _afterUpdateHook(): void {
    this.unlock();
  }

  public updateOptions(): void {
    if (instanceOfOptionProvider<T>(this._optionsProvider) && this.value) {
      Promise.all([this._optionsProvider.getOptions(), this._optionsProvider.getOption(this.value)]).then(
        ([options, optionToPrepend]: [Option<T>[], Option<T>]) => {
          if (!optionToPrepend) {
            this.options = options;
            Logger.error('options-error', 'options to prepend is null');
          } else {
            this.options = options.filter((option: Option<T>) => {
              return option.value !== optionToPrepend.value;
            });
            this.options.unshift(optionToPrepend);
          }
          this._cdr.markForCheck();
        }
      );
    } else {
      this._optionsProvider.getOptions().then((options: Option<T>[]) => {
        this.options = options;
        this._cdr.markForCheck();
      });
    }
  }
}
